import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Tab, Tabs } from '@material-ui/core';

import { Message } from 'components/shared';
import LoadingSpinner from 'components/LoadingSpinner';
import { BenchmarksTab } from 'components/Widget/components/menus/MainSettings/components';
import DashboardSettingsTab from 'components/Widget/components/DashboardSettings/DashboardSettingsTab';
import useFetchCannedDashboard from 'hooks/useFetchCannedDashboard';
import useApi, { USER_SAVE_DASHBOARD } from 'hooks/useApi';

import * as ACTIONS from './reducer/actions';

import { getInitialState, reducer } from './reducer/config';

const useStyles = makeStyles({
  root: {
    flexGrow: 1
  },
  rootInput: {
    '& .MuiOutlinedInput-input': {
      padding: '15px 14px'
    }
  },
  modal_subtitle: {
    marginBottom: '30px'
  }
});

const tabs = [
  { index: 0, label: 'Dashboard Settings' },
  { index: 2, label: 'Series and Benchmarks' }
];

const SaveDashboardPopup = ({ dashboardMenuProps, onClose }) => {
  const { masterObjectId, tabId: dashboardId } = dashboardMenuProps;

  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [popupState, dispatch] = useReducer(reducer, getInitialState(dashboardId));

  const [fetchState, cannedDashboardState] = useFetchCannedDashboard(masterObjectId, dashboardId);
  const { isLoading, isError, error } = fetchState;
  const { dashboardData, widgets } = cannedDashboardState;

  const [saveRequestState, sendRequest] = useApi();
  const {
    data: saveRequestResponse,
    isLoading: isSaveRequestLoading,
    isError: isSaveRequestError,
    error: saveRequestError
  } = saveRequestState;

  useEffect(() => {
    if (isLoading || isError || !dashboardData) {
      return;
    }

    dispatch({ type: ACTIONS.SET_INITIAL_DASHBOARD_STATE, payload: dashboardData });
  }, [isLoading, isError, dashboardData]);

  useEffect(() => {
    if (isSaveRequestLoading || isSaveRequestError) {
      return;
    }

    if (saveRequestResponse) {
      onClose();
    }
  }, [isSaveRequestError, isSaveRequestLoading, onClose, saveRequestResponse]);

  const handlePropUpdate = e => {
    const { target } = e;
    dispatch({
      type: ACTIONS.SET_DASHBOARD_PROPERTY,
      payload: {
        property: target.name,
        value: target.value
      }
    });
  };

  const renderTabs = () =>
    tabs.map(t => (
      <Tab
        label={t.label}
        value={t.index}
        key={t.index}
        disableRipple
        classes={{ root: classes.root }}
      />
    ));

  const handleTabsChange = (_, newValue) => {
    setTab(newValue);
  };

  const submitHandler = () => {
    const { dateRangeTypeId, description, startDate, endDate, id, name, benchmarks } = popupState;
    const params = USER_SAVE_DASHBOARD({
      id,
      name,
      description,
      benchmarks: benchmarks.filter(x => !!x.benchmarkId).map(d => d.benchmarkId),
      dateRangeType: dateRangeTypeId,
      startReportingDate: startDate,
      endReportingDate: endDate,
      masterObjectId: dashboardData.masterObjectId,
      masterObjectTypeId: dashboardData.masterObjectTypeId,
      slots: widgets.length
    });
    sendRequest(params);
  };

  const renderError = () => {
    const errorMessage = error || saveRequestError;
    return <Message type="error" text={errorMessage} />;
  };

  const renderPopupBody = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }
    if (!dashboardData) {
      return null;
    }

    return (
      <>
        {(isError || isSaveRequestError) && renderError()}
        <BenchmarksTab
          value={tab}
          benchmarks={dashboardData.benchmarks}
          handlePropUpdate={handlePropUpdate}
          onSubmit={submitHandler}
          handleClose={onClose}
          dashboardData={dashboardData}
        />
        <DashboardSettingsTab
          value={tab}
          handlePropUpdate={handlePropUpdate}
          onSubmit={submitHandler}
          handleClose={onClose}
          dashboardToUpdate={popupState}
        />
      </>
    );
  };

  return (
    <>
      <Tabs value={tab} variant="fullWidth" indicatorColor="primary" onChange={handleTabsChange}>
        {renderTabs()}
      </Tabs>
      {renderPopupBody()}
    </>
  );
};

SaveDashboardPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dashboardMenuProps: PropTypes.shape({
    masterObjectId: PropTypes.number,
    tabId: PropTypes.string
  }).isRequired,
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({})
  }).isRequired
};

export default SaveDashboardPopup;
